import { Button, Card, Col, Row, Typography, Upload, message, Progress } from "antd";
import { UploadOutlined, LoadingOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { APP_API_URL, INSTITUTION_NAME } from "./utils/constants";
import styles from "./referenceUpload.module.scss";
import { ReactComponent as LogoIcon } from "./assets/James-Hope-University-Logo.svg";
const publicApi = axios.create({
  baseURL: APP_API_URL,
  headers: { "X-Institution": INSTITUTION_NAME || "JHU" },
});

const { Title, Text } = Typography;

const VALID_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const VALID_EXT = /\.(pdf|doc|docx)$/i;
const MAX_SIZE_MB = 10;

export default function ReferenceUpload() {
  const [searchParams] = useSearchParams();

  const applicantName = searchParams.get("applicantName") || "Not specified";
  const referenceNumber = searchParams.get("referenceNumber") || "Not provided";

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!submitted) return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          window.close();
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [submitted]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = async () => {
    if (!file) {
      message.error("Please select a file first");
      return;
    }
    if (referenceNumber === "Not provided") {
      message.error("Invalid reference number in URL");
      return;
    }

    const formData = new FormData();
    formData.append("ReferenceNumber", referenceNumber);
    formData.append("ReferenceFile", file);

    try {
      setLoading(true);
      setUploadProgress(0);

      await publicApi.post(
        "/StudentInfo/StudentInfo/SubmitReferenceLetter",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        }
      );

      setFile(null);
      setUploadProgress(0);
      setSubmitted(true);
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        (error.code === "ECONNABORTED" ? "Upload timeout. Please try again." : error.message) ||
        "Upload failed. Please try again.";
      message.error({ content: msg, duration: 5 });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.container}>
        <Row justify="center" align="middle" style={{ minHeight: "100vh", padding: "20px 0" }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Card className={styles.card}>
              <Row gutter={[0, 24]}>
                <Col span={24} className={styles.logoSection}>
                  <LogoIcon className={styles.logoImg} />
                </Col>
                <Col span={24} style={{ textAlign: "center" }}>
                  <CheckCircleFilled style={{ fontSize: 72, color: "#10b981", marginBottom: 16 }} />
                  <Title level={3} className={styles.title}>
                    Reference Letter Submitted!
                  </Title>
                  <Text className={styles.subtitle}>
                    Thank you for submitting a reference for <strong>{applicantName}</strong>.
                  </Text>
                </Col>
                <Col span={24} style={{ textAlign: "center" }}>
                  <Text className={styles.supportText}>
                    You may now close this page !
                  </Text>
                  { /*<strong>{countdown}</strong> second{countdown !== 2 ? "s" : ""}… */}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Row justify="center" align="middle" style={{ minHeight: "100vh", padding: "20px 0" }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card className={styles.card}>
            <Row gutter={[0, 24]}>

              {/* Logo */}
              <Col span={24} className={styles.logoSection}>
                <div className={styles.logo}>
               <LogoIcon className={styles.logoImg} />                </div>              </Col>

              {/* Title */}
              <Col span={24}>
                <Title level={3} className={styles.title}>
                     James Hope University | Official Reference Submission
                </Title>
                {/* <Text className={styles.subtitle}>
                  James Hope University
                </Text> */}
              </Col>

              {/* Applicant Info */}
              <Col span={24}>
                <div className={styles.infoCard}>
                  <div className={styles.infoRow}>
                    <Text className={styles.infoLabel}>Applicant Name:</Text>
                    <Text className={styles.infoValue}>{applicantName}</Text>
                  </div>
                  <div className={styles.infoRow}>
                    <Text className={styles.infoLabel}>Reference Number:</Text>
                    <Text className={styles.infoValueImportant}>{referenceNumber}</Text>
                  </div>
                </div>
              </Col>

              {/* Upload */}
              <Col span={24}>
                <Text className={styles.uploadLabel}>
                  <strong>Upload Reference Letter</strong>
                  <br />
                  <span className={styles.uploadHint}>PDF, DOC, DOCX • Maximum {MAX_SIZE_MB}MB • Secure upload</span>
                </Text>

                <div className={styles.uploadArea}>
                  <Upload.Dragger
                    name="ReferenceFile"
                    multiple={false}
                    showUploadList={false}
                    accept=".pdf,.doc,.docx"
                    beforeUpload={(selectedFile) => {
                      const isValid =
                        VALID_TYPES.includes(selectedFile.type) ||
                        VALID_EXT.test(selectedFile.name);
                      if (!isValid) {
                        message.error("Only PDF, DOC, or DOCX files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      if (selectedFile.size > MAX_SIZE_MB * 1024 * 1024) {
                        message.error(`File must be smaller than ${MAX_SIZE_MB}MB`);
                        return Upload.LIST_IGNORE;
                      }
                      setFile(selectedFile);
                      message.success(`Selected: ${selectedFile.name}`);
                      return false;
                    }}
                    className={styles.uploadDragger}
                  >
                    <div className={styles.uploadContent}>
                      <UploadOutlined className={styles.uploadIcon} />
                      <div className={styles.uploadText}>
                        {file ? (
                          <>
                            <Text className={styles.fileName} strong>{file.name}</Text>
                            <Text className={styles.fileSize}>{formatFileSize(file.size)}</Text>
                          </>
                        ) : (
                          <>
                            <Text className={styles.uploadPrompt}>Click or drag file to upload</Text>
                            <Text className={styles.uploadSubtext}>PDF, DOC, DOCX (Max {MAX_SIZE_MB}MB)</Text>
                          </>
                        )}
                      </div>
                    </div>
                  </Upload.Dragger>
                </div>

                {uploadProgress > 0 && (
                  <div className={styles.progressContainer}>
                    <Progress percent={uploadProgress} size="small" showInfo status="active" />
                  </div>
                )}
              </Col>

              {/* Submit */}
              <Col span={24}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={!file || loading}
                  block
                  className={styles.submitButton}
                  icon={loading ? <LoadingOutlined /> : null}
                >
                  {loading ? "Uploading..." : "Submit Reference Letter"}
                </Button>
              </Col>

              {/* Footer */}
              <Col span={24}>
                <Text className={styles.thankYou}>Thank you for providing your valuable reference!</Text>
                <Text className={styles.supportText}>
                  Need help? Contact:{' '}
                  <a
                    href="https://admission.jhu.edu.ng/admission"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://admission.jhu.edu.ng/admission
                  </a>
                </Text></Col>

            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
